import { Video, Calendar, Clock, MapPin, ExternalLink, CheckCircle, XCircle, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAdmin } from './AdminContext';

export const AdminBandContentTab = () => {
  const { bandSubmittedShorts, bandSubmittedEvents, handleBandShortAction, handleBandEventAction } = useAdmin();

  return (
    <>
      <h2 className="text-xl font-semibold mb-4">Artist-Submitted Content for Review</h2>
      <p className="text-muted-foreground mb-6">Review and approve shorts and events submitted by artists during registration.</p>

      {/* Shorts */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
          <Video size={18} className="text-secondary" /> Shorts ({bandSubmittedShorts.filter(s => s.status === 'pending').length} pending)
        </h3>
        {bandSubmittedShorts.length === 0 ? (
          <Card className="bg-card border-border"><CardContent className="py-8 text-center text-muted-foreground">No shorts submitted yet</CardContent></Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {bandSubmittedShorts.map(short => (
              <Card key={short.id} className={`bg-card border-border ${short.status === 'pending' ? 'border-yellow-500/50' : short.status === 'approved' ? 'border-green-500/30' : 'border-red-500/30'}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base truncate">{short.title}</CardTitle>
                    <Badge variant={short.status === 'pending' ? 'outline' : short.status === 'approved' ? 'default' : 'destructive'} className="text-xs">{short.status}</Badge>
                  </div>
                  <CardDescription className="text-xs">From: {short.band_name}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <a href={short.video_url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline flex items-center gap-1 truncate"><ExternalLink size={12} /> {short.video_url}</a>
                  {short.description && <p className="text-xs text-muted-foreground line-clamp-2">{short.description}</p>}
                  <p className="text-xs text-muted-foreground">Submitted: {new Date(short.created_at).toLocaleDateString()}</p>
                  {short.status === 'pending' && (
                    <div className="flex gap-2 pt-2">
                      <Button size="sm" variant="outline" className="flex-1 text-xs h-8" onClick={() => handleBandShortAction(short.id, 'approve')}><CheckCircle size={12} className="mr-1" /> Approve</Button>
                      <Button size="sm" variant="ghost" className="text-xs h-8" onClick={() => handleBandShortAction(short.id, 'reject')}><XCircle size={12} className="mr-1" /> Reject</Button>
                    </div>
                  )}
                  <Button size="sm" variant="ghost" className="w-full text-xs h-7 text-destructive hover:text-destructive" onClick={() => handleBandShortAction(short.id, 'delete')}><Trash2 size={12} className="mr-1" /> Delete</Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Events */}
      <div>
        <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
          <Calendar size={18} className="text-green-500" /> Events ({bandSubmittedEvents.filter(e => e.status === 'pending').length} pending)
        </h3>
        {bandSubmittedEvents.length === 0 ? (
          <Card className="bg-card border-border"><CardContent className="py-8 text-center text-muted-foreground">No events submitted yet</CardContent></Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {bandSubmittedEvents.map(event => (
              <Card key={event.id} className={`bg-card border-border ${event.status === 'pending' ? 'border-yellow-500/50' : event.status === 'approved' ? 'border-green-500/30' : 'border-red-500/30'}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base truncate">{event.title}</CardTitle>
                    <Badge variant={event.status === 'pending' ? 'outline' : event.status === 'approved' ? 'default' : 'destructive'} className="text-xs">{event.status}</Badge>
                  </div>
                  <CardDescription className="text-xs">From: {event.band_name}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex items-center gap-1 text-xs"><MapPin size={12} className="text-muted-foreground" /><span className="truncate">{event.venue}</span></div>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><Calendar size={12} /> {event.date}</span>
                    <span className="flex items-center gap-1"><Clock size={12} /> {event.time}</span>
                    <Badge variant="outline" className="text-xs">{event.price_tier}</Badge>
                  </div>
                  {event.description && <p className="text-xs text-muted-foreground line-clamp-2">{event.description}</p>}
                  {event.ticket_url && <a href={event.ticket_url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline flex items-center gap-1"><ExternalLink size={12} /> Tickets</a>}
                  <p className="text-xs text-muted-foreground">Submitted: {new Date(event.created_at).toLocaleDateString()}</p>
                  {event.status === 'pending' && (
                    <div className="flex gap-2 pt-2">
                      <Button size="sm" variant="outline" className="flex-1 text-xs h-8" onClick={() => handleBandEventAction(event.id, 'approve')}><CheckCircle size={12} className="mr-1" /> Approve</Button>
                      <Button size="sm" variant="ghost" className="text-xs h-8" onClick={() => handleBandEventAction(event.id, 'reject')}><XCircle size={12} className="mr-1" /> Reject</Button>
                    </div>
                  )}
                  <Button size="sm" variant="ghost" className="w-full text-xs h-7 text-destructive hover:text-destructive" onClick={() => handleBandEventAction(event.id, 'delete')}><Trash2 size={12} className="mr-1" /> Delete</Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </>
  );
};
